import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createProductDiscountApi,
  deleteProductDiscountApi,
  getProductDiscountApi,
  getProductDiscountsApi,
  toggleProductDiscountApi,
  updateProductDiscountApi,
} from "../../api/ecommerce/ProductDiscountApi";

const initialState = {
  productDiscount: null,
  productDiscounts: [],
  loading: false,
  saving: false,
  deleting: false,
  toggling: false,
  error: null,
};

const getErrorPayload = (error) => ({
  errorMessage:
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong",
});

const createDiscountThunk = (type, api) =>
  createAsyncThunk(type, async (payload, { rejectWithValue }) => {
    try {
      const response = await api(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue(getErrorPayload(error));
    }
  });

export const getProductDiscount = createDiscountThunk(
  "productDiscount/getProductDiscount",
  getProductDiscountApi
);

export const getProductDiscounts = createDiscountThunk(
  "productDiscount/getProductDiscounts",
  getProductDiscountsApi
);

export const createProductDiscount = createDiscountThunk(
  "productDiscount/createProductDiscount",
  createProductDiscountApi
);

export const updateProductDiscount = createDiscountThunk(
  "productDiscount/updateProductDiscount",
  updateProductDiscountApi
);

export const deleteProductDiscount = createDiscountThunk(
  "productDiscount/deleteProductDiscount",
  deleteProductDiscountApi
);

export const toggleProductDiscount = createDiscountThunk(
  "productDiscount/toggleProductDiscount",
  toggleProductDiscountApi
);

const getResponse = (payload) => payload?.response ?? payload;
const getError = (action) => action?.payload?.errorMessage || null;

const productDiscountSlice = createSlice({
  name: "productDiscount",
  initialState,
  reducers: {
    clearProductDiscountError: (state) => {
      state.error = null;
    },
    clearProductDiscountState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductDiscount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProductDiscount.fulfilled, (state, action) => {
        state.productDiscount = getResponse(action.payload);
        state.loading = false;
      })
      .addCase(getProductDiscount.rejected, (state, action) => {
        state.loading = false;
        state.error = getError(action);
      })
      .addCase(getProductDiscounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProductDiscounts.fulfilled, (state, action) => {
        state.productDiscounts = getResponse(action.payload) || [];
        state.loading = false;
      })
      .addCase(getProductDiscounts.rejected, (state, action) => {
        state.loading = false;
        state.error = getError(action);
      })
      .addCase(createProductDiscount.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createProductDiscount.fulfilled, (state, action) => {
        state.productDiscount = getResponse(action.payload);
        state.saving = false;
      })
      .addCase(createProductDiscount.rejected, (state, action) => {
        state.saving = false;
        state.error = getError(action);
      })
      .addCase(updateProductDiscount.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateProductDiscount.fulfilled, (state, action) => {
        state.productDiscount = getResponse(action.payload);
        state.saving = false;
      })
      .addCase(updateProductDiscount.rejected, (state, action) => {
        state.saving = false;
        state.error = getError(action);
      })
      .addCase(deleteProductDiscount.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteProductDiscount.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteProductDiscount.rejected, (state, action) => {
        state.deleting = false;
        state.error = getError(action);
      })
      .addCase(toggleProductDiscount.pending, (state) => {
        state.toggling = true;
        state.error = null;
      })
      .addCase(toggleProductDiscount.fulfilled, (state, action) => {
        state.productDiscount = getResponse(action.payload);
        state.toggling = false;
      })
      .addCase(toggleProductDiscount.rejected, (state, action) => {
        state.toggling = false;
        state.error = getError(action);
      });
  },
});

export const { clearProductDiscountError, clearProductDiscountState } =
  productDiscountSlice.actions;

export default productDiscountSlice.reducer;
